import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MetricCard from '@dashboard/shared/MetricCard';
import RevenueChart from '@dashboard/shared/RevenueChart';
import StatusBadge from '@dashboard/shared/StatusBadge';
import DateRangeSelector, { getFilterDates } from '@dashboard/shared/DateRangeSelector';
import type { DateFilter } from '@dashboard/shared/DateRangeSelector';
import { timeAgo } from '@dashboard/shared/timeAgo';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '@dashboard/hooks/useAuth';
import type { Order, OrderStatus, Merchant } from '@shared/types';

function isSquareOrder(o: Order) {
  return o.provider === 'square' || o.payment_provider === 'square';
}

export default function SquareOverview() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>({ range: '30d' });

  useEffect(() => {
    async function load() {
      const { start, end } = getFilterDates(dateFilter);
      let query = supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (start) query = query.gte('created_at', start);
      if (end) query = query.lte('created_at', end);

      const [ordersRes, merchantRes] = await Promise.all([
        query,
        user
          ? supabase.from('merchants').select('*').eq('id', user.id).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      if (ordersRes.data) {
        setOrders((ordersRes.data as unknown as Order[]).filter(isSquareOrder));
      }
      if (merchantRes.data) setMerchant(merchantRes.data as unknown as Merchant);
      setLoading(false);
    }
    load();

    const channel = supabase
      .channel('square-orders-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, (payload) => {
        const incoming = payload.new as unknown as Order;
        if (!isSquareOrder(incoming)) return;

        if (payload.eventType === 'INSERT') {
          setOrders((prev) => [incoming, ...prev].slice(0, 500));
        } else if (payload.eventType === 'UPDATE') {
          setOrders((prev) => prev.map((o) => (o.id === incoming.id ? incoming : o)));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, dateFilter]);

  const recentOrders = orders.slice(0, 10);

  const todayOrders = orders.filter(
    (o) => new Date(o.created_at).toDateString() === new Date().toDateString()
  );
  const todayRevenue = todayOrders
    .filter((o) => o.status === 'completed')
    .reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0);
  const completedCount = todayOrders.filter((o) => o.status === 'completed').length;
  const successRate = todayOrders.length > 0
    ? ((completedCount / todayOrders.length) * 100).toFixed(0)
    : '0';

  const declinedCount = orders.filter((o) => o.status === 'failed').length;
  const completedOrders = orders.filter((o) => o.status === 'completed');
  const avgOrderValue = completedOrders.length > 0
    ? (completedOrders.reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0) / completedOrders.length)
    : 0;

  const chartData = completedOrders
    .reduce<{ date: string; revenue: number }[]>((acc, o) => {
      const date = new Date(o.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const existing = acc.find((d) => d.date === date);
      if (existing) {
        existing.revenue += o.verified_amount || o.expected_amount;
      } else {
        acc.push({ date, revenue: o.verified_amount || o.expected_amount });
      }
      return acc;
    }, [])
    .reverse();

  const isConnected = !!merchant?.square_onboarded && !!merchant?.square_merchant_id;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>Square Overview</h1>
        <DateRangeSelector value={dateFilter} onChange={setDateFilter} />
      </div>

      {!isConnected && (
        <div
          className="rounded p-4 mb-6 flex items-center justify-between gap-4"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <div>
            <p className="font-sans text-sm font-medium mb-1" style={{ color: 'var(--dash-text-primary)' }}>
              Connect your Square account
            </p>
            <p className="font-sans text-xs" style={{ color: 'var(--dash-text-muted)' }}>
              Authorize BonesPay to accept card payments through Square.
            </p>
          </div>
          <Link
            to="/dashboard/settings"
            className="px-4 py-2 rounded font-mono text-xs uppercase tracking-widest transition-colors shrink-0 text-white"
            style={{ background: '#006AFF' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#0055CC'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#006AFF'; }}
          >
            Connect
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <MetricCard label="Today's Revenue" value={`$${todayRevenue.toFixed(2)}`} />
        <MetricCard label="Today's Orders" value={String(todayOrders.length)} />
        <MetricCard label="Success Rate" value={`${successRate}%`} />
        <MetricCard label="Avg Order" value={`$${avgOrderValue.toFixed(2)}`} />
        <MetricCard
          label="Declined"
          value={String(declinedCount)}
          trend={declinedCount > 0 ? { value: `${declinedCount} failed`, positive: false } : null}
        />
      </div>

      <div className="mb-6">
        <RevenueChart data={chartData} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-dim)' }}>
            Recent Square Orders
          </h2>
          <Link
            to="/dashboard/square/orders"
            className="font-mono text-xs transition-colors"
            style={{ color: 'var(--dash-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
          >
            View all
          </Link>
        </div>

        {loading ? (
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
        ) : recentOrders.length === 0 ? (
          <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
            <div className="text-3xl mb-3 opacity-30">{'\u229E'}</div>
            <p className="font-sans text-sm mb-1" style={{ color: 'var(--dash-text-muted)' }}>
              {isConnected ? 'No Square orders yet' : 'Connect your Square account to start accepting card payments'}
            </p>
            <p className="font-sans text-xs" style={{ color: 'var(--dash-text-faint)' }}>
              {isConnected ? 'Orders will appear here once customers pay with Square.' : 'Head over to Settings to get started.'}
            </p>
          </div>
        ) : (
          <div className="rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
            <table className="w-full text-sm font-sans">
              <thead>
                <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Order</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Amount</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Status</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order, i) => (
                  <tr
                    key={order.id}
                    className="transition-colors"
                    style={{ background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)', borderBottom: '1px solid var(--dash-border-light)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
                  >
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/dashboard/square/orders/${order.id}`}
                        className="font-mono text-xs transition-colors"
                        style={{ color: 'var(--dash-text-tertiary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                      >
                        {order.id.slice(0, 8)}...
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>
                      ${order.expected_amount.toFixed(2)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={order.status as OrderStatus} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                      {timeAgo(order.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
